import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { useTranslations } from "adapters/i18n";
import pendencyService from "features/pendency/pendencyService.js";
import React, { useCallback, useState } from "react";
import { DATE_UNIT, DateUtils } from "shared/dateUtils";
import { NotificationType, type NotificationItem } from "../notification.types";

type UseGetPendingMovementNotificationsProps = {
  toPendingMovementsPage: () => void;
};

export function useGetPendingMovementNotifications({
  toPendingMovementsPage,
}: UseGetPendingMovementNotificationsProps) {
  const { t } = useTranslations();

  const [
    shouldRedirectToPendingMovements,
    setShouldRedirectToPendingMovements,
  ] = useState<boolean>(false);

  const [pendencyOperationDate, setPendencyOperationDate] = useState<
    number | null
  >(null);

  const resetShouldRedirectToPendingMovements = useCallback(() => {
    setShouldRedirectToPendingMovements(false);
  }, []);

  const onPendencyNotificationAction = useCallback(
    (lastPendencyOperationDate: string) => {
      setPendencyOperationDate(
        DateUtils.parse(lastPendencyOperationDate).valueOf(),
      );
      toPendingMovementsPage();
    },
    [toPendingMovementsPage],
  );

  const getPendingMovementsForFarm = useCallback(
    async (farmId: number | null): Promise<NotificationItem[]> => {
      if (farmId == null) {
        return [];
      }

      try {
        const cutoffDate = DateUtils.now().subtract(7, DATE_UNIT.DAY);
        const result = await pendencyService.getLastPendencyByFarmId(farmId);

        if (!result?.data?.data) {
          return [];
        }

        const lastDate = DateUtils.parse(
          result.data.data.operationDate as string,
        );

        if (lastDate.isSameOrBefore(cutoffDate)) {
          return [];
        }

        setPendencyOperationDate((prev) => {
          if (prev == null) {
            return prev;
          }

          const currentDate = DateUtils.parse(prev);
          if (!currentDate.isValid()) {
            return null;
          }

          return currentDate.isBefore(lastDate) ? null : prev;
        });

        return [
          {
            action: () => onPendencyNotificationAction(lastDate.toISOString()),
            icon: <WarningAmberOutlined color="error" />,
            id: NotificationType.PendingMovements,
            title: t("pendency:pending-movements"),
            type: NotificationType.PendingMovements,
          },
        ];
      } catch {
        return [];
      }
    },
    [onPendencyNotificationAction, t],
  );

  return {
    getPendingMovementsForFarm,
    pendencyOperationDate,
    resetShouldRedirectToPendingMovements,
    setPendencyOperationDate,
    shouldRedirectToPendingMovements,
  };
}
